import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Leaf, ShieldCheck, Microphone, HeartStraight, Stethoscope, Warning, CaretDown, Check } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { LANGUAGES, getLang, setLang, tAll } from "@/lib/i18n";

export default function Landing() {
  const [lang, setLangState] = useState(getLang());
  const [open, setOpen] = useState(false);
  const t = tAll(lang);

  useEffect(() => {
    const h = (e) => setLangState(e.detail);
    window.addEventListener("gv-lang-change", h);
    return () => window.removeEventListener("gv-lang-change", h);
  }, []);

  const pick = (code) => { setLang(code); setLangState(code); setOpen(false); };
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[1];

  return (
    <div className="min-h-screen bg-bone text-charcoal grain">
      {/* Nav */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6 gap-3">
        <div>
          <div className="font-heritage text-3xl leading-none text-ayurveda">Gutvibe Health</div>
          <div className="overline text-terracotta mt-1">Food as Medicine · ഭക്ഷണം മരുന്ന്</div>
        </div>
        <nav className="flex items-center gap-2">
          {/* Language selector */}
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-10 border-ayurveda/30 text-charcoal hover:border-ayurveda" data-testid="lang-selector">
                <span className="mr-2">{current.label}</span>
                <CaretDown size={14} />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-1 bg-white">
              <div className="max-h-72 overflow-y-auto">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => pick(l.code)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm rounded-md hover:bg-bone text-charcoal"
                    data-testid={`lang-option-${l.code}`}
                  >
                    <span>
                      <span className="font-medium">{l.label}</span>
                      <span className="text-xs text-charcoal/50 ml-2">{l.english}</span>
                    </span>
                    {l.code === lang && <Check size={14} className="text-terracotta" />}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <Link to="/login" data-testid="landing-login">
            <Button variant="ghost" className="text-charcoal hover:text-ayurveda">{t.signIn}</Button>
          </Link>
          <Link to="/register" data-testid="landing-register">
            <Button className="bg-ayurveda text-bone hover:bg-ayurveda/90">{t.getStarted}</Button>
          </Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative px-6 md:px-12 pt-8 pb-24 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="overline text-terracotta mb-4" data-testid="hero-overline">{t.overline}</div>
          <div className="font-heritage text-2xl text-terracotta mb-2" data-testid="hero-namaste">{t.namaste}</div>
          <h1 className="font-heritage text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-ayurveda" data-testid="hero-welcome">
            {t.welcome}
          </h1>
          <div className="mt-3 font-clinical text-base text-charcoal/70 italic">{t.tagline}</div>
          <div className="mt-6 font-heritage text-3xl md:text-4xl text-charcoal/90 leading-tight">
            {t.hero1} <span className="italic">{t.hero2}</span>
          </div>
          <p className="mt-6 text-base text-charcoal/80 leading-relaxed max-w-lg" data-testid="hero-desc">
            {t.desc}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" data-testid="hero-get-started">
              <Button size="lg" className="bg-ayurveda text-bone hover:bg-ayurveda/90 h-12 px-8">
                {t.ctaPrimary}
              </Button>
            </Link>
            <Link to="/login" data-testid="hero-signin">
              <Button size="lg" variant="outline" className="border-ayurveda text-ayurveda hover:bg-ayurveda hover:text-bone h-12 px-8">
                {t.ctaSecondary}
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex items-center gap-6 text-xs text-charcoal/60">
            <div className="flex items-center gap-2"><ShieldCheck size={16} weight="duotone" /> {t.principles.split(" · ")[0]}</div>
            <div className="flex items-center gap-2"><Microphone size={16} weight="duotone" /> {t.principles.split(" · ")[1]}</div>
            <div className="flex items-center gap-2"><HeartStraight size={16} weight="duotone" /> {t.principles.split(" · ")[2]}</div>
          </div>
        </div>

        {/* Priya — Malayali AI orb + persona label */}
        <div className="relative flex items-center justify-center">
          <motion.div
            animate={{ scale: [1, 1.06, 1], opacity: [0.9, 1, 0.9] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="ai-orb w-72 h-72 md:w-96 md:h-96 rounded-full"
          />
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            className="absolute w-80 h-80 md:w-[26rem] md:h-[26rem] rounded-full border border-terracotta/30"
          />
          <div className="absolute text-center">
            <div className="font-heritage text-bone text-3xl">Priya</div>
            <div className="text-bone/70 text-xs mt-1 italic">{t.listening}</div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-ayurveda text-bone px-6 md:px-12 py-20">
        <div className="max-w-6xl">
          <div className="overline text-terracotta mb-4">Food as Medicine · ഭക്ഷണം മരുന്ന്</div>
          <h2 className="font-heritage text-4xl md:text-5xl mb-10 leading-tight">
            A trusted layer between you<br/> and your care.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Stethoscope, title: "Meet verified doctors", desc: "Book across specialties — general medicine, cardiology, diabetology, pediatrics — with transparent fees." },
              { icon: Leaf, title: "Regional nutrition", desc: "Guidance rooted in Kerala, Malabar & South-Indian food culture — matta rice, meen curry, ragi, moringa." },
              { icon: Warning, title: "Emergency ready", desc: "One-tap SOS, editable contacts and nearby verified hospitals. We never delay emergency care." },
            ].map((f) => (
              <div key={f.title} className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-colors duration-300">
                <f.icon size={32} weight="duotone" className="text-terracotta mb-4" />
                <h3 className="font-clinical text-xl mb-2">{f.title}</h3>
                <p className="text-bone/70 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="px-6 md:px-12 py-20">
        <div className="max-w-4xl">
          <div className="overline text-terracotta mb-4">Our promise</div>
          <p className="font-heritage text-3xl md:text-4xl leading-snug text-ayurveda">
            "Gutvibe Health complements your existing care — Priya, our Malayali AI health companion, listens, gathers, verifies with you, and always keeps a human clinician in the loop. Never diagnoses. Never replaces your doctor."
          </p>
          <div className="mt-8 grid md:grid-cols-3 gap-6 text-sm text-charcoal/70">
            <div><span className="overline text-charcoal">01</span><p className="mt-2">No AI prescriptions or diagnoses outside validated pathways.</p></div>
            <div><span className="overline text-charcoal">02</span><p className="mt-2">Granular consent for camera, microphone, location, sharing.</p></div>
            <div><span className="overline text-charcoal">03</span><p className="mt-2">Voice-first · works in 12 Indian languages.</p></div>
          </div>
        </div>
      </section>

      <footer className="px-6 md:px-12 py-8 border-t border-black/5 text-xs text-charcoal/60">
        © {new Date().getFullYear()} Gutvibe Health · Food as Medicine · ഭക്ഷണം മരുന്ന്. Not a substitute for medical advice.
      </footer>
    </div>
  );
}
